import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, Users, ArrowRight, MessageCircle } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";
import { waLink } from "@/api";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Accommodation({ units, onSelect, config }) {
  const { t, lang } = useLang();
  const rooms = units.filter((u) => u.type === "room");
  const house = units.find((u) => u.type === "house");
  const roomPrice = rooms[0]?.base_price ?? 70;
  const wa = config?.whatsapp || "40721663181";

  return (
    <section className="py-24 md:py-32 bg-cream-light border-y border-warm-grey/30" data-testid="accommodation-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.p {...fadeUp()} className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-walnut flex items-center gap-3">
          <span className="inline-block w-10 h-px bg-terracotta" />
          {t("acc.eyebrow")}
        </motion.p>
        <div className="mt-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <motion.h2 {...fadeUp(0.1)} className="font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-main max-w-xl">
            {t("acc.title")}
          </motion.h2>
          <motion.p {...fadeUp(0.2)} className="text-brown/80 font-light max-w-md">{t("acc.sub")}</motion.p>
        </div>

        {/* PRODUSUL PRINCIPAL: cele 4 camere */}
        <motion.article
          id="camere"
          {...fadeUp(0.15)}
          className="mt-14 group bg-cream border border-warm-grey/40 rounded-3xl overflow-hidden grid lg:grid-cols-2"
          data-testid="card-rooms"
        >
          <div className="relative overflow-hidden aspect-[16/10] lg:aspect-auto">
            <img src="/assets/teracota-1.webp" alt="Cameră Imperfect House cu pat king-size și păturică roz" loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute top-5 left-5 bg-cream-light/90 backdrop-blur rounded-full px-4 py-1.5 text-xs font-bold tracking-[0.15em] uppercase text-brown">
              {t("acc.rooms_tag")}
            </div>
          </div>
          <div className="p-8 md:p-10 flex flex-col">
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-serif text-3xl md:text-4xl text-main">{t("acc.rooms_name")}</h3>
              <div className="text-right shrink-0">
                <p className="text-xs uppercase tracking-widest text-walnut">{t("acc.from")}</p>
                <p className="font-serif text-3xl text-terracotta leading-none">
                  {roomPrice}€<span className="text-base text-brown/60">{t("acc.night")}</span>
                </p>
              </div>
            </div>
            <ul className="mt-7 space-y-3 flex-1">
              {t("acc.rf").map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm md:text-base text-brown/90 font-light">
                  <Check size={17} className="text-green-dark mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            {rooms.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2" data-testid="room-links">
                {rooms.map((r) => (
                  <Link
                    key={r.id}
                    to={`/cazare/${r.slug}`}
                    data-testid={`room-link-${r.slug}`}
                    className="group/link inline-flex items-center gap-1.5 rounded-full border border-warm-grey/60 px-4 py-2 text-xs font-semibold text-brown hover:border-terracotta hover:text-terracotta transition-all duration-300"
                  >
                    {r[`name_${lang}`] || r.name_en || r.name_ro}
                    <ArrowRight size={12} className="group-hover/link:translate-x-0.5 transition-transform" />
                  </Link>
                ))}
              </div>
            )}
            <button
              onClick={() => { onSelect("room"); scrollToId("#rezervare"); }}
              data-testid="card-rooms-cta"
              className="mt-8 rounded-full font-semibold px-7 py-3.5 transition-all duration-300 hover:scale-[1.02] bg-terracotta text-cream-light hover:bg-terracotta/90"
            >
              {t("acc.book_room")}
            </button>
          </div>
        </motion.article>

        {/* SECUNDAR: casa întreagă, discret, la cerere */}
        {house && (
          <motion.div
            id="casa"
            {...fadeUp(0.2)}
            className="mt-8 bg-cream border border-warm-grey/40 rounded-3xl p-7 md:p-8 flex flex-col md:flex-row md:items-center gap-6"
            data-testid="card-house"
          >
            <div className="flex items-start gap-4 flex-1">
              <Users size={22} className="text-green-dark shrink-0 mt-1" />
              <div>
                <h3 className="font-serif text-2xl text-main" data-testid="group-title">{t("acc.group_title")}</h3>
                <p className="mt-2 text-sm text-brown/85 font-light leading-relaxed">{t("acc.group_text")}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-3 shrink-0">
              <a href={waLink(wa, t("acc.group_wa"))} target="_blank" rel="noreferrer" data-testid="card-house-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-main text-cream-light font-semibold px-6 py-3 hover:bg-terracotta transition-all duration-300 text-sm">
                <MessageCircle size={16} /> {t("acc.group_cta")}
              </a>
              <Link to="/cazare/casa-intreaga" data-testid="card-house-link"
                className="text-center text-xs font-semibold text-brown/70 hover:text-terracotta transition-colors">
                {house[`name_${lang}`] || house.name_en || house.name_ro} →
              </Link>
            </div>
          </motion.div>
        )}

        <motion.p {...fadeUp(0.2)} className="mt-10 text-sm text-brown/70 font-light flex items-center gap-2 max-w-2xl" data-testid="acc-note">
          <Users size={16} className="text-walnut shrink-0" />
          {t("acc.note")}
        </motion.p>
      </div>
    </section>
  );
}
